/**
 * Polling utility for waiting on async operations.
 *
 * Polls a function at a fixed interval until it returns a terminal result
 * or the timeout is reached.
 */

export interface PollOptions<T> {
  /** Function to poll. Return `{ done: true, value }` to stop. */
  fn: () => Promise<{ done: boolean; value: T }>
  /** Polling interval in milliseconds. Default: 2000 */
  intervalMs?: number
  /** Timeout in seconds. Default: 300 (5 minutes) */
  timeoutSeconds?: number
  /** Called on each poll with elapsed seconds. */
  onPoll?: (elapsedSeconds: number) => void
}

export async function pollUntilDone<T>(options: PollOptions<T>): Promise<T> {
  const intervalMs = options.intervalMs ?? 2000
  const timeoutMs = (options.timeoutSeconds ?? 300) * 1000
  const start = Date.now()

  while (true) {
    const elapsed = Date.now() - start
    if (elapsed >= timeoutMs) {
      throw new Error(
        `Timed out after ${Math.round(elapsed / 1000)}s waiting for operation to complete.`
      )
    }

    const result = await options.fn()
    if (result.done) {
      return result.value
    }

    options.onPoll?.(Math.round(elapsed / 1000))

    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
}
